import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';
import { getFlapFont } from '../flap-font.js';

// Shared-rig contract (see the framing addendum). 'forbidden': one instanced
// panel of flat quads, no 3D scene for a rig to light - the panel carries its
// own baked lighting term instead (see aLight).
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'splitflap',
  family: 'vector',
  energyLevel: 0.55,
  bpmMin: 80,
  bpmMax: 155,
  movement: 0.50,
  danceability: 0.58,
  tonality: 0.65,
  prefers: { gridStrength: [0.45, 1.0] },
  enabled: true
};

// Splitflap: a Solari departures board that scrolls and settles onto the
// title of whatever is playing.
//
// Reference: the split-flap boards of airports and railway stations. The
// whole pleasure is a scroll that RESOLVES, so the resolution cascade (see
// _resolveText) is the part to look after, not the panel.
//
// HOW THIS DIFFERS FROM type-slam.js, which also renders the title: TypeSlam
// treats the title as brutalist type - full-frame, scrolling, hard, graphic.
// This is a mechanical OBJECT. Characters sit fixed in cells and the
// animation is the flap turning over. Warm and retro rather than graphic and
// dry, which is also why it carries its own POST_PROFILE (below) instead of
// the house grade.
//
// THE MECHANISM, and why it is drawn the way it is.
//
// A cell is two half-flaps, top and bottom, split by a dark median line:
//   progress 0.0 -> 0.5   the TOP half of the outgoing character falls,
//                         foreshortening from full height to nothing
//   progress 0.5 -> 1.0   the TOP half of the incoming character unfolds
//                         back out, with a slap past its stop at the end
//   the BOTTOM half swaps outgoing -> incoming at exactly 0.5, under the
//   shadow the falling top flap casts on it
//
// The foreshortening happens in UV SPACE, not in the geometry: every quad is
// a fixed half-cell and the fragment shader decides how much of it the moving
// face currently covers. Scaling the geometry instead would have dragged the
// cell's own frame and montants around with the flap, and those are precisely
// the parts that must not move - they are the carcass the flap turns inside.
// It also keeps the vertex work constant and every instance buffer static
// apart from the three per-cell numbers that actually animate.
//
// PERFORMANCE SHAPE: 144 cells x 2 half-quads = 288 instances, ONE draw call,
// ONE texture read per pixel. Per frame the CPU writes 288 floats of progress
// (a per-cell value, duplicated to both halves) and, only on frames where a
// flap actually lands, two more short arrays. mediump throughout.
//
// Deliberately NO hash function in the fragment shader. Every per-cell random
// number is drawn on the CPU and handed over as an instance attribute, which
// is both cheaper and immune to the fp16 overflow a coordinate-driven hash
// runs into wherever mediump is genuinely 16-bit - the TV, where a hash21()
// over quad-sized coordinates blackened most of clave.js's textile before it
// was tracked down.

// Per-visualizer post-processing override (POST_DEFAULTS / _applyPostProfile
// in scene-manager.js). A departures board is a lit mechanical object, not a
// light source: the house grade's bloom turns the characters into a haze and
// its chromatic aberration fringes every montant.
export const POST_PROFILE = {
  // A trace, for the glass in front of the flaps. The house 0.0022 fringes
  // the vertical montants, which are the highest-contrast edges here.
  aberration: 0.0008,
  vignette: 0.34,
  // Slightly above the house value: this is a physical object under a lamp,
  // and a little sensor grain is what stops the large flat flap faces from
  // reading as vector fills.
  grain: 0.03,
  // Weak, and just BELOW where the ink lands (see INK_MAX_LINEAR, measured at
  // ~0.82 displayed): so the character faces bleed a trace, the way painted
  // metal under a lamp does on film, and nothing else in the panel comes near
  // it. A Solari character is matte - it must not read as a light source.
  bloomStrength: 0.26,
  bloomThreshold: 0.80
};

const CAMERA_DIST = 8.0;
// The panel overflows the frame - "occupant tout le cadre, debordant
// legerement" - and the amount is not a taste decision: scene-manager applies
// a house-wide camera drift to every visualizer (DRIFT_POSITION_AMOUNT 0.22,
// DRIFT_ROTATION_AMOUNT 0.012 rad), which moves this panel by up to ~30px
// vertically and ~42px horizontally at 1080p. 5% of the frame on each side
// covers that with room to spare; less than that and the panel's own edge
// swings into view, which for a board meant to fill the frame is a defect.
// Overflow is NOT the same on both axes, because the drift is not either:
// scene-manager's wobble is +/-0.22 world on x against +/-0.132 on y (plus
// +/-tan(0.012)*CAMERA_DIST = 0.096 from the rotation on each, and ~1.1% of
// scale from the z wobble). Spending the same 10% on both axes overspent
// vertically, and vertical overflow is expensive here in a way horizontal
// overflow is not: it pushes the outer ROWS off the frame, and those are the
// rows a title has to live on to stay clear of the album cover. So: generous
// on x, just enough on y.
const PANEL_OVERFLOW_X = 1.10;   // ~96px of margin at 1080p, drift needs ~42
const PANEL_OVERFLOW_Y = 1.06;   // ~32px of margin at 1080p, drift needs ~29
// Slack a row's glyph box must keep from the frame edge to count as uncut.
// Deliberately smaller than the full drift excursion: a title that clears the
// STATIC frame and is grazed only at the extremes of a slow wobble is a far
// better outcome than one parked behind the cover, which is the only other
// place to put it when the row count is low (see _chooseTextRows).
const TEXT_ROW_CLIP_MARGIN_WORLD = 0.12;
// Cover overlap counts for more than frame clipping, because the two do
// different damage: the cover HIDES whole characters (it is opaque, and it
// spans the middle ~17% of the width), while the frame edge only trims every
// character's cap. Trimmed type is still readable; missing letters are not.
const TEXT_ROW_COVER_WEIGHT = 2.0;

// One flap turns over in 90ms.
const FLIP_SECONDS = 0.090;
// easeOutBack's own overshoot constant, applied to the DEPLOY half only. At
// 1.2 the flap passes about 4% beyond its stop and settles back exactly on
// it - enough to read as a slap at 90ms, not enough to read as a bounce.
const OVERSHOOT = 1.2;

// The flap's own proportions, which are NOT the cell pitch's. 24 columns over
// 6 rows on a 16:9 frame gives a cell pitch of roughly 1:2.2, and a flap that
// tall would carry a character filling barely half of it. A real board has a
// flap about 1.45x as tall as it is wide, with a generous gap between rows -
// so the flap is derived from the cell WIDTH and the leftover row pitch
// becomes that gap.
// The cell aspect is the same at every tier by construction - 16x4, 20x5 and
// 24x6 all share the same column:row ratio - so one pair of numbers suits all
// three: at 16:9 both terms below agree, and the width-derived one is what
// keeps the flap sane if the frame is ever not 16:9.
const FLAP_ASPECT = 1.95;
const FLAP_W_FRAC = 0.90;      // of the cell pitch width; the rest is montant
const FLAP_H_MAX_FRAC = 0.78;  // the rest of the row pitch is the gap between rows
const GLYPH_FILL = 0.80;       // glyph box height, of the flap height
const GLYPH_W_MAX = 0.94;      // of the flap width

// Resolution cascade. A cell turns through this many characters before it
// settles, the count rising with its column, and starts after a delay that
// also rises with its column - so the text resolves left to right.
const FLIPS_MIN = 4;
const FLIPS_MAX = 12;
const RESOLVE_SPAN_S = 1.50;   // start delay of the LAST column
const ROW_STAGGER_S = 0.025;   // so the two lines do not move in lockstep

// Idle churn, expressed as HOW OFTEN ONE FREE CELL TURNS rather than as a
// count per beat. The count form could not hold a rate: it fired the same
// number of flips whether twenty cells were free or a hundred, so the visible
// churn changed every time the layout did. This is the figure the brief
// actually states - a free cell turns every 6 to 12 seconds - and the count
// per trigger is derived from it and from how many cells are free right now.
const IDLE_PERIOD_MIN_S = 6.0;    // bands.low at full
const IDLE_PERIOD_MAX_S = 12.0;   // quiet
// Free-running (no text to resolve at all): busier, since the churn is then
// the whole composition rather than a background to it.
const FREE_RUN_PERIOD_SCALE = 0.35;
// No usable bar phase (radio, no measured grid): the churn falls back to wall
// clock, so the panel still never freezes.
const IDLE_FALLBACK_S = 0.55;
// Phrase wave: one row, left to right.
const WAVE_STEP_S = 0.035;

// Glyph ranges inside the atlas charset (see tools/build-flapfont.mjs): index
// 0 is SPACE, 1..26 are A-Z, 27..36 are 0-9, 37.. are punctuation.
// Intermediates are drawn from letters and digits only - a cell flicking
// through blanks and commas reads as broken rather than as mechanical.
const SPACE_INDEX = 0;
const SPIN_FIRST = 1;
const SPIN_LAST = 36;

const TIER_LAYOUT = {
  0: { cols: 16, rows: 4, overshoot: false },
  1: { cols: 20, rows: 5, overshoot: true },
  2: { cols: 24, rows: 6, overshoot: true }
};
const DEFAULT_TIER = 1;

// Colours. Warm, and no pure black anywhere: the panel is a lit object, so
// even the carcass between the flaps carries a little of the lamp.
// Set against a MEASURED frame, not picked by eye: a THREE.Color built from a
// hex is read as sRGB and converted to linear, and ACES at exposure 1.1 then
// crushes the bottom of that range hard. The first pass here used values that
// look like a dark warm panel as hex (0x2a2320) and landed at 13/255 on
// screen - a black rectangle with letters on it. These are chosen so the flap
// face lands in the low 0.2s displayed, which is a dark panel under a lamp
// rather than an absence of one.
const CARCASS_COLOR = 0x2b2320;
const FLAP_COLOR = 0x574a40;
const WELL_COLOR = 0x1c1714;
const EDGE_COLOR = 0x9c8a76;   // the flap's own thickness, catching the lamp
const INK_FALLBACK = 0xd8cfc0;
const VIVID_FALLBACK = [0xffb457, 0x6fd0ff, 0xff7a9c];
// Every third row takes its characters from vividColors instead of the
// dominant-tinted off-white. Text rows are exempt - see uTextInk.
const ACCENT_ROW_STRIDE = 3;

// --- VISUAL HIERARCHY ------------------------------------------------------
// Everything below exists for one reason: a board where every cell is equally
// bright is a texture, and the eye has no reason to stop on the title. A real
// departures board greys out the waiting lines and gives full brightness only
// to the imminent one.
//
// Characters on a free cell, against a character on a text row.
const IDLE_INK_LEVEL = 0.35;
const IDLE_INK_SATURATION = 0.55;  // 1 keeps the hue, 0 goes fully grey
// The text rows' own flap faces, lifted so the whole band separates from the
// panel rather than only its characters.
const TEXT_FLAP_GAIN = 1.15;
// ...and their BACKGROUND - the carcass and well around and behind those
// flaps - taken the other way: darker, and pulled towards the artwork's
// dominant colour. A continuous band reads before the characters inside it.
// 0.42, not the 0.72 this started at: the carcass sits at 0.024 in linear
// light, so shaving 28% off it moved the result by five values out of 255 and
// the band simply was not there. What this darkens is exactly the strip above
// and below the text flaps - a text row's own quads draw their own gaps - so
// it frames the band rather than tinting the whole panel.
const TEXT_BACK_DARKEN = 0.25;
const TEXT_BACK_SATURATE = 0.22;
// A text row's characters ignore the panel's lamp falloff. The lamp hangs
// above centre, which handed the lower text row a 22% deficit for no reason
// other than where it happened to land - and on a real board the imminent
// line is the LIT one wherever it sits. The flaps and the carcass keep the
// falloff, so the panel's own lighting stays coherent.
const TEXT_LIGHT_LIFT = 0.65;
// How often an idle turn lands on a BLANK rather than on a character. A real
// board is mostly empty, and the emptiness around the text is what gives it
// room. 0.55 rather than the brief's own "one in two": a coin flip settles at
// 50% occupancy and the brief also asks for 45%, which is the figure you can
// actually see, so the occupancy is what this matches.
const IDLE_BLANK_CHANCE = 0.55;
// Ink cap, in the LINEAR space a THREE.Color holds - and measured, not
// guessed. 0.62 here came out at 0.910 displayed with 2.1% of the panel over
// the bloom threshold: the character faces were smearing. 0.50 lands them at
// roughly 0.82, which keeps the type hard-edged and leaves the palette free
// to change its hue without ever changing its brightness.
const INK_MAX_LINEAR = 0.50;
// And a cap on the BRIGHTEST CHANNEL, not only on luminance. A saturated
// accent passes a luminance test while leaving a channel pinned at 1.0: cyan
// 0x35d1ff is (0.036, 0.639, 1.0) linear, luminance 0.50 - exactly at the cap
// above, so it is not scaled at all, and its blue channel still clips. Aurora
// had the same defect for the same reason (a warm lamp cleared a luma guard
// and clipped red anyway); the fix there and here is to guard on the max
// channel too.
const INK_MAX_CHANNEL = 0.62;
const INK_TINT = 0.35;         // how far the off-white moves toward the palette

// Palette crossfade, at the artwork crossfade's own pace.
const PALETTE_FADE_S = 1.2;

// Row activation from progress: this many rows churn at the start of a track,
// all of them by the end. The text rows are always live.
const ACTIVE_ROWS_MIN = 2;
// ...and at least this many rows that are NOT carrying resolved text, since
// those are the only ones the churn can touch. Without this floor, a track
// early in its progress could have every active row occupied by the title and
// the artist, leaving nothing at all free to move.
const CHURNABLE_ROWS_MIN = 2;

// Which rows come alive first, and in what order. NOT top to bottom: bringing
// rows up as a contiguous block leaves the rest of the panel dead flat for the
// first half of a track, which reads as a broken board rather than a quiet
// one. Farthest-point insertion from the middle outward instead, so however
// many rows are active they are spread over the panel. O(rows^2) on at most
// six rows, run once.
function spreadRowOrder(rows) {
  const order = [Math.floor((rows - 1) / 2)];
  while (order.length < rows) {
    let best = -1;
    let bestDist = -1;
    for (let r = 0; r < rows; r++) {
      if (order.indexOf(r) !== -1) continue;
      let d = Infinity;
      for (let i = 0; i < order.length; i++) d = Math.min(d, Math.abs(r - order[i]));
      if (d > bestDist) { bestDist = d; best = r; }
    }
    order.push(best);
  }
  return order;
}

function clamp01(v) {
  return v < 0 ? 0 : (v > 1 ? 1 : v);
}

function randomSpinIndex() {
  return SPIN_FIRST + Math.floor(Math.random() * (SPIN_LAST - SPIN_FIRST + 1));
}

// Uppercase, with diacritics folded away: the atlas has no accented glyphs
// and French titles are full of them. NFD splits the accent off as a
// combining mark which the range below then drops, so "CAFE" with an acute
// resolves to CAFE rather than to "CAF " - a missing letter is a misread
// title, which is the one thing this visualizer exists to avoid.
function normalizeForBoard(raw) {
  const text = (raw || '').trim().toUpperCase();
  if (text.length === 0) return '';
  try {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch (err) {
    return text;
  }
}

// A board truncates; it does not wrap or shrink. Cut on a word boundary when
// one falls late enough to leave something readable, otherwise hard-cut.
function fitToColumns(text, cols) {
  if (text.length <= cols) return text;
  const boundary = text.lastIndexOf(' ', cols);
  if (boundary >= Math.floor(cols * 0.6)) return text.slice(0, boundary);
  return text.slice(0, cols);
}

const vertexShader = `
  precision mediump float;

  ${SAFE_ZONE_VERTEX_CHUNK}

  uniform vec2 uCellSize;      // world size of one cell PITCH
  uniform float uOvershoot;    // easeOutBack constant, 0 disables it
  uniform vec3 uRowInk[ROW_COUNT];
  uniform float uRowText[ROW_COUNT];  // 1 on a row carrying resolved text
  uniform vec3 uTextInk;              // full-brightness ink for those rows

  attribute vec2 aOffset;      // world centre of this instance's cell
  attribute float aHalf;       // +1 top half, -1 bottom half
  attribute float aRow;
  attribute float aLight;      // baked panel lighting, 0..1
  attribute float aFrom;       // outgoing glyph index
  attribute float aTo;         // incoming glyph index
  attribute float aProgress;   // 0..1 through the turn

  // Packed varyings, three vec4s rather than eight separate declarations: ES2
  // only guarantees eight varying vec4 slots and a separate declaration burns
  // a whole slot each, so they are named back out with #define below and the
  // fragment shader reads as though they were separate.
  varying vec4 vFlapA;
  varying vec4 vFlapB;
  varying vec4 vFlapC;
  // Not packed into the three above: all twelve of their channels are taken,
  // and four varyings is still half of the eight ES2 guarantees.
  varying float vIsText;

  // The flap slaps past its stop and settles back exactly on it: easeOutBack,
  // with 0 collapsing to a plain smooth ease for tier 0 (no overshoot there).
  float flapEase(float t) {
    float u = t - 1.0;
    return 1.0 + (uOvershoot + 1.0) * u * u * u + uOvershoot * u * u;
  }

  void main() {
    // Which character this half shows, and how much of its own height it
    // currently covers - the two-stage mechanism, in full.
    float glyph;
    float scaleY;
    if (aHalf > 0.0) {
      if (aProgress < 0.5) {
        // Falling. 1 - t*t rather than 1 - t: a flap comes off its stop
        // slowly and accelerates, and a linear ramp reads as a fade.
        float t = aProgress * 2.0;
        glyph = aFrom;
        scaleY = 1.0 - t * t;
      } else {
        float t = (aProgress - 0.5) * 2.0;
        glyph = aTo;
        scaleY = flapEase(t);
      }
    } else {
      // The bottom half does not turn: it swaps at exactly 0.5, under the
      // shadow of the flap falling in front of it.
      glyph = aProgress < 0.5 ? aFrom : aTo;
      scaleY = 1.0;
    }

    vec3 world = vec3(
      aOffset.x + position.x * uCellSize.x,
      aOffset.y + aHalf * position.y * uCellSize.y * 0.5,
      0.0
    );
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    gl_Position = clip;

    // Atlas cell -> uv origin of this glyph's content box. Computed rather
    // than looked up: the atlas is a regular grid, so a uniform array of 49
    // rectangles would spend vertex uniform slots saying what two multiplies
    // already say. The four constants are baked from the manifest when the
    // material is built, so a rebuilt atlas cannot silently disagree with it.
    float gx = mod(glyph, ATLAS_GRID);
    float gy = floor(glyph / ATLAS_GRID);
    vec2 glyphUv0 = vec2(ATLAS_U0 + gx * ATLAS_STEP, ATLAS_V0 - gy * ATLAS_STEP);

    // A text row's characters are the FULL-brightness ink, and deliberately
    // not this row's own palette entry: an accent row that happened to fall
    // under the title would have rendered it in cyan, where the brief asks
    // for a franc white. Everywhere else the row's own colour survives, but
    // dimmed and pulled towards grey - which is what stops a hundred idle
    // characters from competing with the two lines that matter.
    float isText = uRowText[int(aRow)];
    float inkLight = aLight + (1.0 - aLight) * ${TEXT_LIGHT_LIFT.toFixed(3)} * isText;
    vec3 rowInk = uRowInk[int(aRow)];
    float rowLuma = dot(rowInk, vec3(0.299, 0.587, 0.114));
    vec3 idleInk = mix(vec3(rowLuma), rowInk, ${IDLE_INK_SATURATION.toFixed(3)}) * ${IDLE_INK_LEVEL.toFixed(3)};
    vec3 ink = mix(idleInk, uTextInk, isText);

    vFlapA = vec4(uv.x, uv.y, glyphUv0.x, glyphUv0.y);
    vFlapB = vec4(ink * inkLight, scaleY);
    vFlapC = vec4(aHalf, aProgress, aLight, safeZoneAttenuation(clip));
    vIsText = isText;
  }
`;

const fragmentShader = `
  precision mediump float;

  uniform sampler2D uAtlas;
  uniform float uHasAtlas;
  uniform vec2 uFlapExtent;    // (half-width, height) in local cell units
  uniform vec2 uGlyphSize;     // glyph box, as a fraction of the cell pitch
  uniform vec2 uContentUv;     // size of one glyph's content box, in uv
  uniform vec3 uCarcass;
  uniform vec3 uCarcassText;   // the text rows' own, darker and more saturated
  uniform vec3 uFlap;
  uniform vec3 uWell;
  uniform vec3 uWellText;
  uniform vec3 uEdge;

  varying vec4 vFlapA;
  varying vec4 vFlapB;
  varying vec4 vFlapC;
  varying float vIsText;

  #define vLocal    vFlapA.xy
  #define vGlyphUv0 vFlapA.zw
  #define vInk      vFlapB.rgb
  #define vScaleY   vFlapB.w
  #define vHalf     vFlapC.x
  #define vProgress vFlapC.y
  #define vLight    vFlapC.z
  #define vSafeAtt  vFlapC.w

  // Edge softness, in local cell units. Fixed rather than derived from
  // fwidth(): a GLSL ES 1.00 shader needs GL_OES_standard_derivatives
  // explicitly enabled, and one texel of softness on a montant is not worth
  // depending on an extension for.
  const float FEATHER_X = 0.020;
  const float FEATHER_Y = 0.016;
  const float MEDIAN_HALF = 0.030;
  const float MEDIAN_DARK = 0.30;
  const float EDGE_THICKNESS = 0.045;
  const float FLAP_GRADIENT = 0.16;
  const float FACE_TURN_SHADE = 0.30;
  const float SHADOW_STRENGTH = 0.55;
  const float SHADOW_REACH = 0.34;

  // 1 below the edge, 0 above it, softened over feather.
  //
  // Written as 1 - smoothstep(lo, hi, y) rather than smoothstep(hi, lo, y).
  // The inverted-argument form is UNDEFINED in GLSL ES - the spec requires
  // edge0 < edge1 - and it only happens to invert cleanly on the drivers that
  // implement it as a plain clamped ratio. Relying on that is how a shader
  // ends up correct on desktop and wrong on the TV.
  float below(float edge, float y, float feather) {
    return 1.0 - smoothstep(edge, edge + feather, y);
  }

  void main() {
    vec2 L = vLocal;

    // --- the carcass, behind everything ---------------------------------
    // Darker and warmer under a text row: the band is meant to read as one
    // continuous highlighted line, before any of its characters do.
    vec3 col = mix(uCarcass, uCarcassText, vIsText) * (0.70 + 0.30 * vLight);

    // --- the flap's static footprint: outside it, the montants ----------
    float gapX = 0.5 - uFlapExtent.x;
    float inX = smoothstep(gapX, gapX + FEATHER_X, L.x)
              * (1.0 - smoothstep(1.0 - gapX - FEATHER_X, 1.0 - gapX, L.x));
    float footprint = inX * below(uFlapExtent.y, L.y, FEATHER_Y);

    // The well: the inside of the mechanism, visible wherever the moving face
    // has retracted from. Darkest toward the median, where it is deepest.
    col = mix(col, mix(uWell, uWellText, vIsText) * (0.55 + 0.45 * L.y) * vLight, footprint);

    // --- the moving face -------------------------------------------------
    float faceTop = uFlapExtent.y * vScaleY;
    float face = inX * below(faceTop, L.y, FEATHER_Y);

    // Un-foreshorten: where on the flap's OWN surface this pixel sits. The
    // face covers only faceTop of the cell but still carries the whole
    // character, squashed into it - which is what a flap tipped away from you
    // looks like, and the reason this reads as a turn and not as a crossfade.
    float surfaceY = (L.y * 0.5) / max(vScaleY, 0.004);
    vec2 g = vec2(
      (L.x - 0.5) / uGlyphSize.x + 0.5,
      (vHalf * surfaceY) / uGlyphSize.y + 0.5
    );
    // Clamped and masked rather than branched: one unconditional texture
    // read, no divergence, and nothing sampled outside the content box.
    float inBox = step(0.0, g.x) * step(g.x, 1.0) * step(0.0, g.y) * step(g.y, 1.0);
    float ink = texture2D(uAtlas, vGlyphUv0 + clamp(g, 0.0, 1.0) * uContentUv).a
              * inBox * uHasAtlas;

    // A surface turning away from the lamp loses light.
    float turn = mix(FACE_TURN_SHADE, 1.0, vScaleY);
    // The text rows' flaps are lifted, so the band detaches even where it
    // carries no character at all.
    vec3 flapCol = uFlap * mix(1.0, ${TEXT_FLAP_GAIN.toFixed(3)}, vIsText)
                 * vLight * turn * (1.0 - FLAP_GRADIENT * L.y);
    flapCol = mix(flapCol, vInk * turn, ink);

    // The flap's own thickness: a thin lit line along its leading edge.
    float edge = below(faceTop, L.y, FEATHER_Y)
               * smoothstep(faceTop - EDGE_THICKNESS - FEATHER_Y, faceTop - EDGE_THICKNESS, L.y);
    flapCol += uEdge * edge * vLight * 0.8;

    col = mix(col, flapCol, face);

    // --- the median line, fixed, never moves with the flap ---------------
    col *= mix(1.0, MEDIAN_DARK, below(MEDIAN_HALF, L.y, FEATHER_Y));

    // --- the falling flap's shadow on the bottom half --------------------
    // Peaks at mid-turn, which is also the instant the bottom half swaps: the
    // swap happens under the shadow rather than in plain sight.
    float shadowAmt = (1.0 - abs(2.0 * vProgress - 1.0)) * step(vHalf, 0.0);
    col *= 1.0 - SHADOW_STRENGTH * shadowAmt * (1.0 - smoothstep(0.0, SHADOW_REACH, L.y));

    gl_FragColor = vec4(col * vSafeAtt, 1.0);
  }
`;

export class SplitflapVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // Options first, then the shared preload - the same self-serving fallback
    // pattern loom.js/shoji.js use for the paper texture, so a construction
    // site that knows nothing about the atlas still gets one.
    this._font = (options && options.flapFont) || getFlapFont() || null;
    this.postProcessing = POST_PROFILE;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._cols = layout.cols;
    this._rows = layout.rows;
    this._cellCount = this._cols * this._rows;
    this._instanceCount = this._cellCount * 2;
    this._rowOrder = spreadRowOrder(this._rows);
    this._rowActive = new Uint8Array(this._rows);
    this._rowGutter = new Uint8Array(this._rows);
    // Scratch for _scatterIdle's eligible-cell pass - allocated once, so the
    // churn never allocates per beat.
    this._idlePool = new Int16Array(this._cellCount);
    // How many columns the TEXT may use. The panel deliberately overflows the
    // frame, so its outermost columns are partly off-screen - laying a title
    // across all of them would clip its first and last characters, which is
    // exactly the "sans coupure" the brief rules out. The text is still
    // centred across the full grid; it is only its LENGTH that is budgeted.
    this._textCols = Math.max(1, Math.floor(this._cols / PANEL_OVERFLOW_X));

    // --- per-cell simulation state --------------------------------------
    // INVARIANT: when a cell is not turning, _cur === _next === what is on
    // screen. _startFlip only moves _next; landing copies it back into _cur.
    // Getting that backwards left every cell displaying the character it had
    // just turned AWAY from.
    this._cur = new Int16Array(this._cellCount);
    this._next = new Int16Array(this._cellCount);
    this._prog = new Float32Array(this._cellCount);
    this._flipping = new Uint8Array(this._cellCount);
    this._delay = new Float32Array(this._cellCount);
    this._remaining = new Int16Array(this._cellCount);
    // The glyph a resolution run must land on, or -1 for "anything" - which
    // is the idle churn, and the whole panel when there is no text at all.
    this._final = new Int16Array(this._cellCount);
    for (let i = 0; i < this._cellCount; i++) this._final[i] = -1;
    // Which rows the title and the second line resolved onto, so the churn
    // and the phrase wave leave them alone.
    this._textRows = [];

    this._time = 0;
    this._frozen = false;
    this._lastBeat = null;
    this._lastAccentPhrase = null;
    this._lastIdleAt = 0;
    this._lastScatterAt = 0;
    // Fractional carry for the churn rate: at one free cell per twelve
    // seconds a beat is worth a fraction of a flip, and rounding that to zero
    // every time would stop the panel entirely.
    this._flipDebt = 0;
    this._lastTrackId = null;
    this._lastTextKey = null;
    this._freeRunning = true;
    this.flipsStarted = 0;
    this._activeRows = this._rows;
    this._glyphsDirty = true;
    this._syncActiveRows(this._rows);

    this._buildPalette();
    this._buildGeometry();
    this._buildMaterial();
    this._layout();

    // Something mechanical has to be on screen before the first poll lands -
    // and already at the target occupancy, so the panel never starts out as
    // the wall of characters the hierarchy work exists to get rid of.
    for (let i = 0; i < this._cellCount; i++) {
      this._cur[i] = Math.random() < IDLE_BLANK_CHANCE ? SPACE_INDEX : randomSpinIndex();
      this._next[i] = this._cur[i];
    }
    this._scatterIdle(1.0, IDLE_PERIOD_MAX_S);
    this._uploadGlyphs();
    this._uploadProgress();
  }

  // --- geometry ------------------------------------------------------------

  _buildGeometry() {
    // Base quad anchored on the MEDIAN, not on its own centre: y runs 0..1
    // from the median outward and aHalf mirrors it for the bottom half. That
    // is what lets one geometry serve both halves and makes "measure from the
    // median" a plain multiply in both shaders.
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0
    ]), 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
      0, 0, 1, 0, 1, 1, 0, 1
    ]), 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);

    const n = this._instanceCount;
    this._aOffset = new THREE.InstancedBufferAttribute(new Float32Array(n * 2), 2);
    this._aHalf = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._aRow = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._aLight = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._aFrom = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._aTo = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
    this._aProgress = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);

    geometry.setAttribute('aOffset', this._aOffset);
    geometry.setAttribute('aHalf', this._aHalf);
    geometry.setAttribute('aRow', this._aRow);
    geometry.setAttribute('aLight', this._aLight);
    geometry.setAttribute('aFrom', this._aFrom);
    geometry.setAttribute('aTo', this._aTo);
    geometry.setAttribute('aProgress', this._aProgress);
    geometry.instanceCount = n;

    // Instance layout: cell c owns instances 2c (top half) and 2c+1 (bottom).
    // Every per-cell upload below writes both, which is why nothing here ever
    // needs an index indirection.
    for (let cell = 0; cell < this._cellCount; cell++) {
      const row = Math.floor(cell / this._cols);
      this._aHalf.array[cell * 2] = 1;
      this._aHalf.array[cell * 2 + 1] = -1;
      this._aRow.array[cell * 2] = row;
      this._aRow.array[cell * 2 + 1] = row;
    }
    this._aHalf.needsUpdate = true;
    this._aRow.needsUpdate = true;

    this._geometry = geometry;
  }

  _buildMaterial() {
    const meta = this._font ? this._font.meta : null;
    // Atlas geometry, baked into the shader source from the manifest - the
    // same "sizing known only at runtime, so pass it in rather than write it
    // twice" rule clave.js's textile follows. A rebuilt atlas with a
    // different grid therefore cannot silently disagree with the shader.
    const size = meta ? meta.size : 1024;
    const grid = meta ? meta.grid : 8;
    const cell = meta ? meta.cell : size / grid;
    const contentW = meta ? meta.contentWidth : 88;
    const contentH = meta ? meta.contentHeight : 120;
    const boxX = Math.round((cell - contentW) / 2);
    const boxY = Math.round((cell - contentH) / 2);
    const half = 0.5 / size;

    const source = vertexShader
      .replace(/ROW_COUNT/g, String(this._rows))
      .replace(/ATLAS_GRID/g, grid.toFixed(1))
      .replace(/ATLAS_STEP/g, (cell / size).toFixed(8))
      .replace(/ATLAS_U0/g, (boxX / size + half).toFixed(8))
      // v0 is the BOTTOM of the content box, and v grows upward while the
      // atlas's own rows grow downward - hence the subtraction on gy.
      .replace(/ATLAS_V0/g, (1 - (boxY + contentH) / size + half).toFixed(8));

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uAtlas: { value: this._font ? this._font.texture : null },
      uHasAtlas: { value: this._font ? 1 : 0 },
      uCellSize: { value: new THREE.Vector2(1, 1) },
      uFlapExtent: { value: new THREE.Vector2(FLAP_W_FRAC * 0.5, 0.6) },
      uGlyphSize: { value: new THREE.Vector2(0.8, 0.5) },
      uContentUv: { value: new THREE.Vector2(
        contentW / size - 2 * half,
        contentH / size - 2 * half
      ) },
      uOvershoot: { value: TIER_LAYOUT[this._tier].overshoot ? OVERSHOOT : 0 },
      uCarcass: { value: new THREE.Color(CARCASS_COLOR) },
      uFlap: { value: new THREE.Color(FLAP_COLOR) },
      uWell: { value: new THREE.Color(WELL_COLOR) },
      uEdge: { value: new THREE.Color(EDGE_COLOR) },
      uRowInk: { value: this._rowInk },
      uRowText: { value: this._rowText },
      uTextInk: { value: this._textInk },
      uCarcassText: { value: this._carcassText },
      uWellText: { value: this._wellText }
    });

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: source,
      fragmentShader: fragmentShader,
      // The panel covers the frame and overflows it, and its cells tile
      // without overlapping: opaque, no blending, no depth work needed.
      transparent: false,
      depthTest: false,
      depthWrite: false,
      // DoubleSide is NOT decorative here. The bottom half is the same quad
      // mirrored through the median (aHalf = -1), and mirroring one axis
      // reverses the triangle winding - so under the default FrontSide every
      // bottom half was back-facing and silently culled. The panel rendered
      // as six thin strips, each one a top half alone, and the flap read at
      // half its computed height because only half of it existed.
      side: THREE.DoubleSide
    });
    this._mesh = new THREE.Mesh(this._geometry, this._material);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -1000;
    this._scene.add(this._mesh);
  }

  // Cell pitch, flap proportions, glyph box and cell centres, all derived
  // from the real frustum at the panel's depth - re-run whenever the aspect
  // changes (see update()), never written down as a constant.
  _layout() {
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const halfH = CAMERA_DIST * Math.tan(fovRad / 2);
    const halfW = halfH * this._camera.aspect;
    const panelW = 2 * halfW * PANEL_OVERFLOW_X;
    const panelH = 2 * halfH * PANEL_OVERFLOW_Y;
    const cellW = panelW / this._cols;
    const cellH = panelH / this._rows;
    this._uniforms.uCellSize.value.set(cellW, cellH);

    const flapW = cellW * FLAP_W_FRAC;
    const flapH = Math.min(flapW * FLAP_ASPECT, cellH * FLAP_H_MAX_FRAC);
    // uFlapExtent is in the half-quad's own local units: x is a half-width
    // over the cell pitch width, y is a height over the HALF cell pitch -
    // which is the same number as flapH/cellH.
    this._uniforms.uFlapExtent.value.set(FLAP_W_FRAC * 0.5, flapH / cellH);

    // Glyph box, preserving the atlas content box's aspect in WORLD units:
    // fitted to the flap's height, then narrowed if that made it too wide.
    const meta = this._font ? this._font.meta : null;
    const atlasAspect = meta ? meta.contentWidth / meta.contentHeight : 88 / 120;
    let glyphH = flapH * GLYPH_FILL;
    let glyphW = glyphH * atlasAspect;
    const maxW = flapW * GLYPH_W_MAX;
    if (glyphW > maxW) {
      glyphW = maxW;
      glyphH = glyphW / atlasAspect;
    }
    this._uniforms.uGlyphSize.value.set(glyphW / cellW, glyphH / cellH);

    // Per-row legibility inputs for _chooseTextRows, measured here where the
    // real frustum is already in hand: how much of the row's GLYPH BOX the
    // frame edge cuts, and where that box sits in the same "height units"
    // the safe zone uses. The panel deliberately overflows the frame, so its
    // outermost rows ARE cut through the characters - the first pass resolved
    // the title onto exactly those rows (they are the ones clear of the
    // cover) and the second line came out sliced along its baseline.
    if (!this._rowClipFrac || this._rowClipFrac.length !== this._rows) {
      this._rowClipFrac = new Float32Array(this._rows);
      this._rowGlyphTop = new Float32Array(this._rows);
      this._rowGlyphBottom = new Float32Array(this._rows);
    }
    const limit = halfH - TEXT_ROW_CLIP_MARGIN_WORLD;
    for (let row = 0; row < this._rows; row++) {
      const median = panelH / 2 - (row + 0.5) * cellH;
      const top = median + glyphH / 2;
      const bottom = median - glyphH / 2;
      const cut = Math.max(0, top - limit) + Math.max(0, -limit - bottom);
      this._rowClipFrac[row] = glyphH > 0 ? Math.min(1, cut / glyphH) : 1;
      this._rowGlyphTop[row] = top / halfH;
      this._rowGlyphBottom[row] = bottom / halfH;
    }

    // Cell centres, and the baked lighting term: a broad soft falloff from
    // above centre, as though one lamp were hung over the board. Constant per
    // cell, so it costs nothing per frame - and it is what makes the panel
    // read as a lit object rather than as a flat fill.
    for (let cell = 0; cell < this._cellCount; cell++) {
      const col = cell % this._cols;
      const row = Math.floor(cell / this._cols);
      const x = -panelW / 2 + (col + 0.5) * cellW;
      const y = panelH / 2 - (row + 0.5) * cellH;
      const nx = (col + 0.5) / this._cols - 0.5;
      const ny = (row + 0.5) / this._rows - 0.35;
      const fall = Math.sqrt(nx * nx * 1.1 + ny * ny * 1.5);
      const light = 0.66 + 0.34 * clamp01(1 - fall * 1.25);
      for (let h = 0; h < 2; h++) {
        const i = cell * 2 + h;
        this._aOffset.array[i * 2] = x;
        this._aOffset.array[i * 2 + 1] = y;
        this._aLight.array[i] = light;
      }
    }
    this._aOffset.needsUpdate = true;
    this._aLight.needsUpdate = true;
    this._appliedAspect = this._camera.aspect;
  }

  // --- palette -------------------------------------------------------------

  _buildPalette() {
    this._rowInk = [];
    this._rowInkFrom = [];
    this._rowInkTo = [];
    // 1 on a row carrying resolved text. A plain array of numbers, which is
    // what Three wants for a float[] uniform.
    this._rowText = [];
    this._textInk = new THREE.Color(INK_FALLBACK);
    this._carcassText = new THREE.Color(CARCASS_COLOR);
    this._wellText = new THREE.Color(WELL_COLOR);
    for (let row = 0; row < this._rows; row++) {
      this._rowInk.push(new THREE.Color(INK_FALLBACK));
      this._rowInkFrom.push(new THREE.Color(INK_FALLBACK));
      this._rowInkTo.push(new THREE.Color(INK_FALLBACK));
      this._rowText.push(0);
    }
    this._paletteFadeStart = -Infinity;
    this._paletteFadeDone = false;
    this._lastPaletteRef = undefined;
  }

  // Characters are an off-white TINTED by dominantColors[0]; every third row
  // takes a vividColors entry outright. Both are capped in luminance, so a
  // bright cover can change the board's hue but never push it into the bloom.
  _resolveRowInk(profile) {
    const palette = profile.artworkPalette;
    if (palette === this._lastPaletteRef) return;
    this._lastPaletteRef = palette;

    const dominant = (palette && palette.dominantColors) || [];
    const vivid = (palette && palette.vividColors) || [];
    const base = new THREE.Color(INK_FALLBACK);
    if (dominant.length > 0) base.lerp(new THREE.Color(dominant[0]), INK_TINT);

    // The text rows' ink: the base, at full brightness, capped the same way
    // the row inks are so a bright cover can still only change its hue.
    this._textInk.copy(base);
    const textLum = 0.299 * this._textInk.r + 0.587 * this._textInk.g + 0.114 * this._textInk.b;
    const textPeak = Math.max(this._textInk.r, Math.max(this._textInk.g, this._textInk.b));
    const textScale = Math.min(
      textLum > INK_MAX_LINEAR ? INK_MAX_LINEAR / textLum : 1,
      textPeak > INK_MAX_CHANNEL ? INK_MAX_CHANNEL / textPeak : 1
    );
    if (textScale < 1) this._textInk.multiplyScalar(textScale);

    // And their background. Not crossfaded with the palette like the inks
    // are: the carcass is the darkest thing on screen and a snap in it is
    // invisible, where a snap in the characters would not be.
    //
    // The tint is brought DOWN to the target's own luminance before it is
    // mixed in, so the mix moves the hue and nothing else. Mixing a saturated
    // colour in directly does not "saturate" a dark surface, it floods it:
    // the carcass sits at 0.024 in linear light and a vivid orange at 1.0, so
    // even a 22% mix came out seven times brighter and the text rows were
    // framed by a bright ochre bar instead of the darker band the brief asks
    // for. Darkening afterwards could not undo that.
    const backTint = dominant.length > 0
      ? new THREE.Color(dominant[0])
      : new THREE.Color(VIVID_FALLBACK[0]);
    this._carcassText.set(CARCASS_COLOR);
    this._wellText.set(WELL_COLOR);
    const tintLum = 0.299 * backTint.r + 0.587 * backTint.g + 0.114 * backTint.b;
    for (let i = 0; i < 2; i++) {
      const target = i === 0 ? this._carcassText : this._wellText;
      const targetLum = 0.299 * target.r + 0.587 * target.g + 0.114 * target.b;
      const matched = backTint.clone();
      if (tintLum > 0.001) matched.multiplyScalar(targetLum / tintLum);
      target.lerp(matched, TEXT_BACK_SATURATE).multiplyScalar(TEXT_BACK_DARKEN);
    }

    for (let row = 0; row < this._rows; row++) {
      const target = this._rowInkTo[row];
      if (row % ACCENT_ROW_STRIDE === ACCENT_ROW_STRIDE - 1) {
        const pool = vivid.length > 0 ? vivid : VIVID_FALLBACK;
        target.set(pool[Math.floor(row / ACCENT_ROW_STRIDE) % pool.length]);
      } else {
        target.copy(base);
      }
      const lum = 0.299 * target.r + 0.587 * target.g + 0.114 * target.b;
      const peak = Math.max(target.r, Math.max(target.g, target.b));
      const scale = Math.min(
        lum > INK_MAX_LINEAR ? INK_MAX_LINEAR / lum : 1,
        peak > INK_MAX_CHANNEL ? INK_MAX_CHANNEL / peak : 1
      );
      if (scale < 1) target.multiplyScalar(scale);
      this._rowInkFrom[row].copy(this._rowInk[row]);
    }
    this._paletteFadeStart = this._time;
    // Re-armed here and nowhere else: left set from the previous fade, every
    // later palette change would have snapped instead of crossfading.
    this._paletteFadeDone = false;
  }

  _tickPalette() {
    if (this._paletteFadeDone) return;
    const t = clamp01((this._time - this._paletteFadeStart) / PALETTE_FADE_S);
    for (let row = 0; row < this._rows; row++) {
      this._rowInk[row].copy(this._rowInkFrom[row]).lerp(this._rowInkTo[row], t);
    }
    if (t >= 1) this._paletteFadeDone = true;
  }

  // --- the resolution cascade ---------------------------------------------

  // Title, then artist, then album - a radio stream routinely carries none of
  // them, and per the brief an empty title must leave the panel scrolling
  // freely rather than write a placeholder into it. Returns null for that
  // case, which is what puts _freeRunning on.
  _resolveLabels(profile) {
    const title = normalizeForBoard(cleanDisplayTitle(profile.title))
      || normalizeForBoard(cleanDisplayArtist(profile.artist))
      || normalizeForBoard(profile.album);
    if (!title) return null;
    // The second line is whichever of artist/album is not already the first.
    const artistRaw = normalizeForBoard(cleanDisplayArtist(profile.artist));
    const albumRaw = normalizeForBoard(profile.album);
    let second = artistRaw && artistRaw !== title ? artistRaw : '';
    if (!second && albumRaw && albumRaw !== title) second = albumRaw;
    return { title: title, artist: second };
  }

  // Two rows, centred, and chosen for LEGIBILITY rather than fixed. Two
  // things can make a row unreadable and both have to be avoided:
  //
  //   - the album cover, an OPAQUE DOM overlay across the middle of the
  //     frame. The safe zone only attenuates what is behind it, it cannot
  //     make it readable, so a title on a middle row loses its middle third.
  //   - the frame edge. The panel overflows deliberately, so its outermost
  //     rows are cut through the characters - measured at 1920x1080 with six
  //     rows, the top and bottom rows lose about 20% of the glyph box, and
  //     the camera drift can take more.
  //
  // Those two pull in opposite directions: the rows clear of the cover are
  // exactly the rows the overflow cuts. _rowSafe (see _layout) settles it -
  // only fully visible rows are candidates, and the cover then picks between
  // them. At 1920x1080 with 24x6 that lands on rows 1 and 4, which overlap
  // the measured cover by ~5% of their height and only across the middle 17%
  // of the width. Title on the upper one, second line on the lower: on a
  // departures board that is also the natural order.
  _chooseTextRows() {
    // R19: the rows are chosen against the stage centre, not the cover.
    const centre = safeZoneUniforms.uStageCenter.value.y;
    const half = safeZoneUniforms.uStageHalfSize.value.y;
    const zoneTop = centre + half;
    const zoneBottom = centre - half;
    let above = -1;
    let abovePenalty = Infinity;
    let below = -1;
    let belowPenalty = Infinity;
    for (let row = 0; row < this._rows; row++) {
      const top = this._rowGlyphTop[row];
      const bottom = this._rowGlyphBottom[row];
      const span = top - bottom;
      // Both penalties are fractions of the GLYPH BOX, not of the row, so
      // they are directly comparable and neither is inflated by the dead
      // space between rows.
      const covered = Math.max(0, Math.min(top, zoneTop) - Math.max(bottom, zoneBottom));
      const coverFrac = span > 0 ? covered / span : 1;
      const penalty = coverFrac * TEXT_ROW_COVER_WEIGHT + this._rowClipFrac[row];
      const mid = (top + bottom) / 2;
      if (mid >= centre) {
        if (penalty < abovePenalty) { abovePenalty = penalty; above = row; }
      } else if (penalty < belowPenalty) {
        belowPenalty = penalty;
        below = row;
      }
    }
    if (above < 0) above = below;
    if (below < 0) below = above;
    if (above < 0) return [0];
    return above === below ? [above] : [above, below];
  }

  // "une rangee entierement vide au-dessus et en dessous des rangees de titre
  // et d'artiste" - the gutter that isolates each line.
  //
  // Taken literally at 24x6 this blanks EVERY remaining row: with the title
  // on row 1 and the artist on row 4, rows 0, 2, 3 and 5 are all adjacent to
  // one of them. That would leave nothing carrying a character outside the
  // text, which makes three of the brief's own other points inoperative at
  // once - the 35% ink level, the 45% occupancy, and "le mouvement doit
  // rester perceptible en peripherie". So gutters are released, outermost
  // first, until enough rows are left to churn: the ones kept are those
  // BETWEEN the text rows, which is where the churn would otherwise sit
  // right up against a line, and the ones released are against the frame
  // edge, where the edge itself already separates the text from anything.
  _computeGutterRows(textRows) {
    const gutter = new Uint8Array(this._rows);
    for (let i = 0; i < textRows.length; i++) {
      const r = textRows[i];
      if (r - 1 >= 0) gutter[r - 1] = 1;
      if (r + 1 < this._rows) gutter[r + 1] = 1;
    }
    for (let i = 0; i < textRows.length; i++) gutter[textRows[i]] = 0;

    const isText = (row) => textRows.indexOf(row) !== -1;
    const countFree = () => {
      let n = 0;
      for (let row = 0; row < this._rows; row++) if (!gutter[row] && !isText(row)) n++;
      return n;
    };
    let free = countFree();
    if (free < CHURNABLE_ROWS_MIN) {
      const centre = (this._rows - 1) / 2;
      const held = [];
      for (let row = 0; row < this._rows; row++) if (gutter[row]) held.push(row);
      held.sort((a, b) => Math.abs(b - centre) - Math.abs(a - centre));
      for (let i = 0; i < held.length && free < CHURNABLE_ROWS_MIN; i++) {
        gutter[held[i]] = 0;
        free++;
      }
    }
    return gutter;
  }

  // Fills the two text rows COMPLETELY - the characters of the text where it
  // reaches, blanks where it does not. Every cell in those rows therefore
  // resolves and settles: a title sitting inside a row that is still churning
  // reads as a glitch, not as a board.
  _resolveText(profile) {
    const labels = this._resolveLabels(profile);
    const rows = this._chooseTextRows();
    this._textRows = labels ? rows : [];
    this._freeRunning = !labels;
    this._lastTextKey = labels ? labels.title + ' ' + labels.artist : '';
    this._rowGutter = labels
      ? this._computeGutterRows(rows)
      : new Uint8Array(this._rows);
    for (let row = 0; row < this._rows; row++) {
      this._rowText[row] = this._isTextRow(row) ? 1 : 0;
    }

    for (let cell = 0; cell < this._cellCount; cell++) this._final[cell] = -1;

    if (labels) {
      const lines = [labels.title, labels.artist];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const text = fitToColumns(lines[i] || '', this._textCols);
        const start = Math.floor((this._cols - text.length) / 2);
        for (let col = 0; col < this._cols; col++) {
          const ch = col >= start && col < start + text.length ? text[col - start] : ' ';
          this._final[row * this._cols + col] = this._glyphIndex(ch);
        }
      }
      // Gutter rows resolve to blank and stay there: a target of SPACE is
      // also what keeps them out of the churn, since every idle pass skips
      // any cell that still has one.
      for (let row = 0; row < this._rows; row++) {
        if (!this._rowGutter[row]) continue;
        for (let col = 0; col < this._cols; col++) {
          this._final[row * this._cols + col] = SPACE_INDEX;
        }
      }
    }

    // Launch every cell. Text cells run to their target; the rest get one
    // scatter flip, so the WHOLE panel moves at a track change and then falls
    // back to the idle churn.
    for (let cell = 0; cell < this._cellCount; cell++) {
      const col = cell % this._cols;
      const row = Math.floor(cell / this._cols);
      const colFrac = this._cols > 1 ? col / (this._cols - 1) : 0;
      if (this._rowGutter[row]) {
        // One turn, straight to blank. A gutter row scrolling through
        // characters on its way to being empty would be the opposite of what
        // it is for.
        this._remaining[cell] = 1;
        this._delay[cell] = colFrac * RESOLVE_SPAN_S * 0.5;
      } else if (this._final[cell] >= 0) {
        // More intermediates the further right the cell is, so the left of a
        // line settles while its right is still turning.
        this._remaining[cell] = Math.round(FLIPS_MIN + (FLIPS_MAX - FLIPS_MIN) * colFrac);
        this._delay[cell] = colFrac * RESOLVE_SPAN_S + row * ROW_STAGGER_S;
      } else {
        this._remaining[cell] = 1;
        this._delay[cell] = colFrac * RESOLVE_SPAN_S * 0.7 + Math.random() * 0.4;
      }
      // Restores the not-turning invariant for any cell caught mid-flip by a
      // retitle: without this the cell would snap back to _cur for a frame.
      this._next[cell] = this._cur[cell];
      this._flipping[cell] = 0;
      this._prog[cell] = 0;
    }
    this._glyphsDirty = true;
  }

  _glyphIndex(ch) {
    if (!this._font) return SPACE_INDEX;
    const idx = this._font.charIndex.get(ch);
    return idx == null ? SPACE_INDEX : idx;
  }

  // --- the churn -----------------------------------------------------------

  // Marks the first `count` rows of _rowOrder active, plus every text row -
  // a resolved line's own row is live from the moment it resolves, whatever
  // the track progress says. Recomputed unconditionally each frame: it is at
  // most six writes, and a dirty flag here would only be somewhere for the
  // text rows to go stale.
  _syncActiveRows(count) {
    this._rowActive.fill(0);
    // Free-running (no text at all): the churn IS the composition, so every
    // row is live regardless of progress.
    const target = this._freeRunning ? this._rows : count;
    let marked = 0;
    for (let i = 0; i < this._rowOrder.length && marked < target; i++) {
      this._rowActive[this._rowOrder[i]] = 1;
      marked++;
    }
    for (let i = 0; i < this._textRows.length; i++) this._rowActive[this._textRows[i]] = 1;
    // Then top up until enough CHURNABLE (active, non-text) rows exist - see
    // CHURNABLE_ROWS_MIN.
    // Gutter rows count as un-churnable alongside text rows: their cells hold
    // a SPACE target, so every idle pass skips them, and topping up with one
    // would leave the panel just as still as before.
    let churnable = 0;
    for (let row = 0; row < this._rows; row++) {
      if (this._rowActive[row] && !this._isTextRow(row) && !this._rowGutter[row]) churnable++;
    }
    for (let i = 0; i < this._rowOrder.length && churnable < CHURNABLE_ROWS_MIN; i++) {
      const row = this._rowOrder[i];
      if (this._rowActive[row] || this._isTextRow(row) || this._rowGutter[row]) continue;
      this._rowActive[row] = 1;
      churnable++;
    }
  }

  _isTextRow(row) {
    for (let i = 0; i < this._textRows.length; i++) {
      if (this._textRows[i] === row) return true;
    }
    return false;
  }

  // Turns over however many free cells it takes to keep EACH free cell
  // turning once every `periodS` on average, given that `intervalS` has
  // passed since the last pass. Expressed as a rate rather than as a count
  // because a count cannot hold one: the same number of flips per beat reads
  // as a busy panel over twenty free cells and a dead one over a hundred, and
  // the brief asks for a per-cell period ("toutes les 6 a 12 s").
  //
  // The eligible cells are COLLECTED first, then drawn from. Sampling the
  // whole grid and discarding the misses looks equivalent and is not: early
  // in a track only a couple of rows are active and the text and gutter rows
  // are excluded, so five picks in six were being thrown away and the panel
  // stood still for seconds at a time. Collecting costs one pass over 144
  // cells, once per beat.
  _scatterIdle(intervalS, periodS) {
    const pool = this._idlePool;
    let n = 0;
    for (let cell = 0; cell < this._cellCount; cell++) {
      if (this._final[cell] >= 0) continue;
      if (!this._rowActive[Math.floor(cell / this._cols)]) continue;
      if (this._flipping[cell] || this._remaining[cell] > 0 || this._delay[cell] > 0) continue;
      pool[n++] = cell;
    }
    if (n === 0) return;
    this._flipDebt += (n * Math.max(0, intervalS)) / Math.max(periodS, 0.5);
    const take = Math.min(n, Math.floor(this._flipDebt));
    if (take <= 0) return;
    this._flipDebt -= take;
    // Partial Fisher-Yates over the pool: `take` distinct cells, no retries.
    for (let k = 0; k < take; k++) {
      const j = k + Math.floor(Math.random() * (n - k));
      const cell = pool[j];
      pool[j] = pool[k];
      pool[k] = cell;
      this._remaining[cell] = 1;
      // A little jitter inside the beat rather than every pick on one frame:
      // a board's departures do not all change in lockstep.
      this._delay[cell] = Math.random() * 0.08;
    }
  }

  // "sur chaque temps fort de phrase, une vague de basculements traverse le
  // panneau de gauche a droite, sur une rangee tiree au hasard" - a resolved
  // text row is never chosen, that would undo the cascade.
  _phraseWave() {
    const candidates = [];
    for (let row = 0; row < this._rows; row++) {
      if (this._rowActive[row] && !this._isTextRow(row) && !this._rowGutter[row]) candidates.push(row);
    }
    if (candidates.length === 0) return;
    const row = candidates[Math.floor(Math.random() * candidates.length)];
    for (let col = 0; col < this._cols; col++) {
      const cell = row * this._cols + col;
      if (this._final[cell] >= 0 || this._flipping[cell]) continue;
      this._remaining[cell] = 1;
      this._delay[cell] = col * WAVE_STEP_S;
    }
  }

  _startFlip(cell) {
    // Read by the acceptance harness: counting flips STARTED is frame-rate
    // independent, where sampling _flipping is not - below ~11fps a 90ms turn
    // begins and ends inside one frame and a sampler never sees it.
    this.flipsStarted++;
    const landing = this._remaining[cell] <= 1 && this._final[cell] >= 0;
    if (landing) {
      this._next[cell] = this._final[cell];
    } else if (this._final[cell] >= 0) {
      // Mid-way through a resolution run: always a character, so the cell
      // reads as scrolling towards its target rather than blinking.
      this._next[cell] = randomSpinIndex();
    } else {
      // A free cell. Blank as often as not - "sur un vrai tableau, la
      // majorite est vide", and the emptiness around the text is what gives
      // the text its place.
      this._next[cell] = Math.random() < IDLE_BLANK_CHANCE ? SPACE_INDEX : randomSpinIndex();
    }
    this._flipping[cell] = 1;
    this._prog[cell] = 0;
    this._glyphsDirty = true;
  }

  _advance(dt) {
    for (let cell = 0; cell < this._cellCount; cell++) {
      if (this._delay[cell] > 0) {
        this._delay[cell] -= dt;
        if (this._delay[cell] > 0) continue;
        this._delay[cell] = 0;
      }
      if (!this._flipping[cell]) {
        if (this._remaining[cell] <= 0) continue;
        this._startFlip(cell);
      }
      this._prog[cell] += dt / FLIP_SECONDS;
      if (this._prog[cell] < 1) continue;
      // Landed: the incoming character is now the one on screen.
      this._cur[cell] = this._next[cell];
      this._prog[cell] = 0;
      this._flipping[cell] = 0;
      this._remaining[cell] -= 1;
      this._glyphsDirty = true;
      // Chain straight into the next turn of the same run, so a 12-flip cell
      // reads as one continuous scroll rather than as 12 separate flips.
      if (this._remaining[cell] > 0) this._startFlip(cell);
    }
  }

  // "pause -> gel, volets figes a mi-course". Cells already turning hold
  // where they are; the rest are pushed to mid-turn, so the whole panel reads
  // as a mechanism stopped in motion rather than as a still image.
  _freeze() {
    for (let cell = 0; cell < this._cellCount; cell++) {
      if (this._flipping[cell]) continue;
      this._next[cell] = randomSpinIndex();
      this._flipping[cell] = 1;
      this._prog[cell] = 0.5;
      this._delay[cell] = 0;
      if (this._remaining[cell] <= 0) this._remaining[cell] = 1;
    }
    this._glyphsDirty = true;
    this._uploadGlyphs();
    this._uploadProgress();
  }

  // --- uploads -------------------------------------------------------------

  _uploadProgress() {
    const arr = this._aProgress.array;
    for (let cell = 0; cell < this._cellCount; cell++) {
      const p = this._flipping[cell] ? this._prog[cell] : 0;
      arr[cell * 2] = p;
      arr[cell * 2 + 1] = p;
    }
    this._aProgress.needsUpdate = true;
  }

  _uploadGlyphs() {
    const from = this._aFrom.array;
    const to = this._aTo.array;
    for (let cell = 0; cell < this._cellCount; cell++) {
      const a = this._cur[cell];
      const b = this._next[cell];
      from[cell * 2] = a;
      from[cell * 2 + 1] = a;
      to[cell * 2] = b;
      to[cell * 2 + 1] = b;
    }
    this._aFrom.needsUpdate = true;
    this._aTo.needsUpdate = true;
    this._glyphsDirty = false;
  }

  // --- frame ---------------------------------------------------------------

  update(deltaTime, profile) {
    this._time += deltaTime;
    const director = profile.director || {};

    if (this._camera.aspect !== this._appliedAspect) this._layout();

    // playbackState as well as onPlayPause: the callback is the signal, this
    // is the guarantee - a visualizer swapped in while already paused never
    // received one.
    const paused = profile.playbackState !== 'PLAYING';
    if (paused !== this._frozen) {
      this._frozen = paused;
      if (paused) this._freeze();
    }

    this._resolveRowInk(profile);
    this._tickPalette();

    if (this._frozen) return;

    // progress -> how many rows churn. The board fills out as the track runs;
    // the text rows are live from the start either way.
    const span = Math.max(0, this._rows - ACTIVE_ROWS_MIN);
    this._activeRows = Math.min(this._rows,
      ACTIVE_ROWS_MIN + Math.round(span * clamp01(profile.progress)));
    this._syncActiveRows(this._activeRows);

    // Sonos fills the title in a poll or two AFTER announcing a track, and a
    // stream retitles without the track id ever changing - so the cascade
    // triggers on the resolved text, not only on trackId.
    const labels = this._resolveLabels(profile);
    const textKey = labels ? labels.title + ' ' + labels.artist : '';
    if (profile.trackId !== this._lastTrackId || textKey !== this._lastTextKey) {
      this._lastTrackId = profile.trackId;
      this._resolveText(profile);
    }

    // bands.low shortens the period rather than adding flips - same signal,
    // same direction, but now expressed in the unit the brief states.
    const low = director.bands ? director.bands.low || 0 : 0;
    let period = IDLE_PERIOD_MAX_S + (IDLE_PERIOD_MIN_S - IDLE_PERIOD_MAX_S) * clamp01(low);
    if (this._freeRunning) period *= FREE_RUN_PERIOD_SCALE;

    // "les basculements aleatoires tombent sur les temps" - the BEAT's wrap is
    // the trigger, not the bar's. Firing once per bar left three quarters of
    // every bar with nothing moving at all (measured: up to 3.4s still at
    // 112bpm), which criterion 3 rules out; the beat is also the more literal
    // reading of "sur les temps".
    const beat = director.beat;
    if (beat != null) {
      if (this._lastBeat != null && beat < this._lastBeat) {
        this._scatterIdle(this._time - this._lastScatterAt, period);
        this._lastScatterAt = this._time;
      }
      this._lastBeat = beat;
      this._lastIdleAt = this._time;
    } else if (this._time - this._lastIdleAt >= IDLE_FALLBACK_S) {
      // No grid at all (radio): wall clock, so the panel keeps moving
      // regardless of whether the track was ever analysed.
      this._lastIdleAt = this._time;
      this._scatterIdle(this._time - this._lastScatterAt, period);
      this._lastScatterAt = this._time;
    }

    // phrase -> one wave across a random row, on its accent.
    const accent = director.accentPhrase;
    if (accent != null) {
      if (this._lastAccentPhrase != null && accent > this._lastAccentPhrase) this._phraseWave();
      this._lastAccentPhrase = accent;
    }

    this._advance(deltaTime);
    if (this._glyphsDirty) this._uploadGlyphs();
    this._uploadProgress();
  }

  onTrackChange(profile) {
    // update() drives the cascade off trackId and the resolved text itself,
    // so this only has to make sure the next frame re-resolves even when the
    // id repeats - a replay of the same track.
    this._lastTrackId = null;
    this._lastTextKey = null;
  }

  onPlayPause(playbackState) {
    const paused = playbackState !== 'PLAYING';
    if (paused === this._frozen) return;
    this._frozen = paused;
    if (paused) this._freeze();
  }

  dispose() {
    if (this._mesh) {
      this._scene.remove(this._mesh);
      this._mesh = null;
    }
    if (this._geometry) {
      this._geometry.dispose();
      this._geometry = null;
    }
    if (this._material) {
      this._material.dispose();
      this._material = null;
    }
    // The atlas is shared and preloaded (see flap-font.js): never disposed
    // here.
    this._font = null;
  }
}
